/**
 * Pipeline Project Service
 *
 * Manages per-session state for the Requirements → Design → Docs → Code →
 * Tests → CI/CD → Deploy pipeline. State lives in sessionStorage so it
 * survives page refreshes within a browser tab but is cleared when the tab
 * is closed.
 */

const STORAGE_KEY = 'pipeline_v1';
const AWS_CONFIG_KEY = 'pipeline_aws_config_v1';

const STAGE_IDS = ['requirements', 'design', 'docs', 'code', 'tests', 'cicd', 'deployment'];

const emptyStage = () => ({
  data: null,
  approved: false,
  comment: '',
  jobId: null,
});

const defaultProject = () => ({
  sessionId: Date.now().toString(36),
  createdAt: new Date().toISOString(),
  stages: Object.fromEntries(STAGE_IDS.map((id) => [id, emptyStage()])),
});

// ── Listeners ────────────────────────────────────────────────────────────────

const _listeners = new Set();

const _notify = (project) => {
  _listeners.forEach((fn) => fn(project));
};

/**
 * Subscribe to project changes.
 * Returns an unsubscribe function suitable for useEffect cleanup.
 */
export const subscribe = (fn) => {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
};

// ── Persistence ───────────────────────────────────────────────────────────────

const _persist = (project) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(project));
  } catch {
    // sessionStorage might be full or unavailable — continue in-memory
  }
  _notify(project);
  return project;
};

// ── Public API ────────────────────────────────────────────────────────────────

/** Read the current project (or a fresh default). */
export const getProject = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Back-fill any new stage ids that didn't exist when the project was saved
      STAGE_IDS.forEach((id) => {
        if (!parsed.stages[id]) parsed.stages[id] = emptyStage();
      });
      return parsed;
    }
  } catch {
    // Corrupt storage — start fresh
  }
  return defaultProject();
};

/** Overwrite one stage's full data object. */
export const setStageData = (stageId, data) => {
  const project = getProject();
  project.stages[stageId] = {
    ...project.stages[stageId],
    data,
    jobId: null,
  };
  return _persist(project);
};

/** Patch specific keys inside a stage (does not clear jobId). */
export const patchStage = (stageId, patch) => {
  const project = getProject();
  project.stages[stageId] = { ...project.stages[stageId], ...patch };
  return _persist(project);
};

/** Set the approval state and optional comment for a stage. */
export const approveStage = (stageId, approved, comment = '') => {
  return patchStage(stageId, { approved, comment });
};

/** Update the user comment without changing approval. */
export const setStageComment = (stageId, comment) => {
  return patchStage(stageId, { comment });
};

/** Mark a stage as having a pending async job. */
export const setStageJob = (stageId, jobId) => {
  return patchStage(stageId, { jobId, data: null });
};

/** Get a single stage's state. */
export const getStage = (stageId) => {
  return getProject().stages[stageId] || emptyStage();
};

/** Clear everything and start a fresh pipeline session. */
export const clearProject = () => {
  sessionStorage.removeItem(STORAGE_KEY);
  const fresh = defaultProject();
  _notify(fresh);
  return fresh;
};

/**
 * Build the cumulative context payload sent to each stage agent.
 * Later stages receive the confirmed outputs of all earlier stages.
 */
export const buildStageContext = (upToStageId) => {
  const project = getProject();
  const idx = STAGE_IDS.indexOf(upToStageId);
  const context = {};
  STAGE_IDS.slice(0, idx).forEach((id) => {
    const stage = project.stages[id];
    if (stage.data) context[id] = stage.data;
  });
  // Always include AWS config so agents can tailor output to the user's account
  const awsCfg = getAwsConfig();
  if (awsCfg) context.awsConfig = awsCfg;
  return context;
};

// ── AWS Config ────────────────────────────────────────────────────────────────

/** Save AWS onboarding config to localStorage (persists across tabs). */
export const saveAwsConfig = (config) => {
  try {
    localStorage.setItem(AWS_CONFIG_KEY, JSON.stringify(config));
  } catch {}
  _listeners.forEach((fn) => fn(getProject()));
};

/** Read AWS config (null if not set yet). */
export const getAwsConfig = () => {
  try {
    const raw = localStorage.getItem(AWS_CONFIG_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/** Clear saved AWS config. */
export const clearAwsConfig = () => {
  try { localStorage.removeItem(AWS_CONFIG_KEY); } catch {}
};
